export function generatePassword(length: number = 12): string {
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
  const numberChars = "0123456789";
  const specialChars = "!@#$%^&*()_+[]{}<>?,./";

  // Ensure password has at least one of each type of character
  const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;

  // Function to get a random character from a given string
  const getRandomChar = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  // Initialize password with one character from each required set
  const password = [
    getRandomChar(uppercaseChars),
    getRandomChar(lowercaseChars),
    getRandomChar(numberChars),
    getRandomChar(specialChars),
  ];

  // Fill the rest of the password length with random characters
  for (let i = 4; i < length; i++) {
    password.push(getRandomChar(allChars));
  }

  // Shuffle the password to ensure randomness
  return password.sort(() => 0.5 - Math.random()).join("");
}

export function isNotNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isTuple<T>(array: T[]): array is [T, ...T[]] {
  return array.length > 0;
}

export function nonNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function areObjectsEqual<T extends Record<string, unknown>>(
  obj1: T,
  obj2: T,
): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) => {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (typeof val1 === "object" && typeof val2 === "object") {
      return areObjectsEqual(
        val1 as Record<string, unknown>,
        val2 as Record<string, unknown>,
      );
    }

    return val1 === val2;
  });
}

export function unproxify<T>(val: T): T {
  if (val instanceof Array) return val.map(unproxify) as T;
  if (val instanceof Object)
    return Object.fromEntries(
      Object.entries(Object.assign({}, val)).map(([k, v]) => [k, unproxify(v)]),
    ) as T;
  return val;
}

/**
 * Extract a cookie value from a cookie string
 * @param cookieString The cookie string from the request header
 * @param name The name of the cookie to extract
 * @returns The cookie value or null if not found
 */
export function getCookieValue(
  cookieString: string,
  name: string,
): string | null {
  const match = cookieString.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

/**
 * Get the app URL with runtime hostname preference
 * @param request Optional request object to extract hostname from
 * @returns The app URL (runtime hostname preferred, fallback to VITE_APP_URL)
 */
export function getAppUrl(request?: Request): string {
  if (request) {
    return new URL(request.url).origin;
  }

  const envUrl = process.env.VITE_APP_URL ?? import.meta.env.VITE_APP_URL;
  return envUrl ?? "http://localhost:3000";
}

/**
 * Truncate text to a specified length and add ellipsis if needed
 * @param text The text to truncate
 * @param maxLength The maximum length before truncation
 * @param suffix The suffix to add when truncated (default: "...")
 * @returns The truncated text with suffix if needed
 */
export function truncateText(
  text: string,
  maxLength: number,
  suffix: string = "...",
): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + suffix;
}

// ==========================================
// General-purpose data helpers
// ==========================================

/**
 * Return the first non-empty trimmed string from a list of values.
 */
export function firstNonEmpty(
  ...values: Array<string | null | undefined>
): string {
  for (const v of values) {
    const s = (v ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Normalize a string or string[] into string[]; return undefined for empty input.
 */
export function toStringArray(
  value: string | string[] | undefined,
): string[] | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) return value.map((v) => v.trim()).filter(Boolean);
  const single = value.trim();
  return single ? [single] : undefined;
}

/**
 * Generate initials from a name or multiple name options
 * @param names Array of potential name values (first non-empty will be used)
 * @param fallback Fallback initials if no valid name is found
 * @returns Initials string (e.g., "JD" for "John Doe")
 */
export function getInitials(
  names: Array<string | null | undefined>,
  fallback: string = "??",
): string {
  const name = firstNonEmpty(...names);

  if (!name) {
    return fallback;
  }

  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}
